/**
 * Erkennung und einmalige Selbstheilung von Chunk-Ladefehlern.
 *
 * Hintergrund (Befund 13.10.2026): Nach einem Prod-Deploy liefert Cloudflare
 * bis zum Post-Deploy-Purge (~10 min) noch altes HTML aus; dasselbe gilt für
 * Tabs, die über einen Deploy hinweg offen bleiben. Das alte HTML bzw. die
 * alte Router-Instanz fordert Lazy-Chunks an, die es im neuen Build nicht
 * mehr gibt. Der Origin antwortet darauf mit HTML statt JavaScript, in der
 * Konsole stehen `SyntaxError: Unexpected token '<'` und danach
 * `ChunkLoadError: Loading chunk 8205 failed`. Die Seite fällt in die
 * Error-Boundary. Ein einmaliger harter Reload mit Cache-Buster holt frisches
 * HTML vom Origin und heilt den Zustand.
 *
 * Wo der Fehler ankommt (Next.js 16.3.5, node_modules/next/dist/client/components):
 * - Ein fehlgeschlagenes Chunk-Promise wirft React beim Rendern der
 *   Lazy-Komponente; die nächste Error-Boundary fängt es
 *   (`error-boundary.js` → `ErrorBoundaryHandler.getDerivedStateFromError`),
 *   also `error.tsx` bzw. `global-error.tsx`.
 * - React 19 meldet GEFANGENE Fehler nur per `console.error`
 *   (`react-dom-client.production.js` → `defaultOnCaughtError`); nur
 *   UNGEFANGENE Fehler gehen per `reportError` an `window`
 *   (`defaultOnUncaughtError`). Ein globaler `window`-Listener für
 *   `error`/`unhandledrejection` sähe Boundary-Fehler deshalb nie — die
 *   Heilung hängt darum an den Error-Boundaries, nicht an `window`.
 * - Nexts eigener Mechanismus (`nav-failure-handler.js` →
 *   `handleHardNavError`) ist nur hinter dem Experimental-Flag
 *   `appNavFailHandling` aktiv und greift ausschließlich, wenn eine
 *   Navigation zu einer ANDEREN URL aussteht — nicht beim Nachladen auf der
 *   aktuellen Seite und nicht beim Erstaufruf aus altem HTML.
 *
 * Fehlertexte (Quellen im Bundle):
 * - Webpack-Runtime (`next/dist/compiled/webpack/bundle5.js`):
 *   `Loading chunk <id> failed.\n(<typ>: <url>)` mit `error.name = 'ChunkLoadError'`
 * - mini-css-extract-plugin: `Loading CSS chunk <id> failed.\n(<href>)`
 * - Natives `import()`: Chrome `Failed to fetch dynamically imported module`,
 *   Firefox `error loading dynamically imported module`,
 *   Safari `Importing a module script failed`
 * - Turbopack: `Failed to load chunk …` (Vorsorge, der Build läuft mit --webpack)
 *
 * Schleifenschutz: höchstens EIN Reload pro Minute und Pfad. Merker liegt in
 * `sessionStorage` (Tab-gebunden) UND als In-Memory-Marker des Dokuments, das
 * aus dem Reload entstanden ist (`consumeReloadMarkerFromUrl`). Letzterer
 * wirkt auch dort, wo `sessionStorage` nicht verfügbar ist (privater Modus,
 * blockierte Site-Daten): Ein Dokument, das selbst schon der Reload war,
 * löst nie einen zweiten aus.
 */

/** Query-Parameter, der Cloudflare zu einem frischen Origin-Abruf zwingt. */
export const RELOAD_PARAM = 'mg_reload';

/** Innerhalb dieses Fensters wird pro Pfad kein zweiter Reload versucht. */
export const RELOAD_WINDOW_MS = 60_000;

const STORAGE_KEY_PREFIX = 'mg:chunk-reload:';

const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
    /Loading (?:CSS )?chunk \S+ failed/i,
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Failed to load chunk/i,
];

function matchesChunkPattern(text: string): boolean {
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Erkennt Chunk-Ladefehler robust: über `error.name`, den Nachrichtentext und
 * rekursiv über `error.cause` (begrenzte Tiefe). Nimmt auch rohe Strings an,
 * wie sie als `reason` einer Promise-Rejection vorkommen können.
 */
export function isChunkLoadError(error: unknown, depth = 0): boolean {
    if (error == null || depth > 3) return false;
    if (typeof error === 'string') return matchesChunkPattern(error);
    if (typeof error !== 'object') return false;
    const {name, message, cause} = error as {name?: unknown; message?: unknown; cause?: unknown};
    if (name === 'ChunkLoadError') return true;
    if (typeof message === 'string' && matchesChunkPattern(message)) return true;
    return isChunkLoadError(cause, depth + 1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Schleifenschutz
// ─────────────────────────────────────────────────────────────────────────────

/** Marker des laufenden Dokuments: Es ist selbst aus einem Reload entstanden. */
let documentReloadMarker: {pathname: string; at: number} | null = null;

function safeStorage(): Storage | null {
    try {
        if (typeof window === 'undefined') return null;
        return window.sessionStorage ?? null;
    } catch {
        // Privater Modus / blockierte Site-Daten: Zugriff kann werfen.
        return null;
    }
}

function storageKey(pathname: string): string {
    return `${STORAGE_KEY_PREFIX}${pathname}`;
}

/** Nur für Tests: In-Memory-Marker zurücksetzen. */
export function resetChunkReloadStateForTests(): void {
    documentReloadMarker = null;
}

export function hasRecentReloadAttempt(pathname: string, now: number = Date.now()): boolean {
    if (documentReloadMarker && documentReloadMarker.pathname === pathname
        && now - documentReloadMarker.at < RELOAD_WINDOW_MS) {
        return true;
    }
    const storage = safeStorage();
    if (!storage) return false;
    try {
        const raw = storage.getItem(storageKey(pathname));
        if (raw === null) return false;
        const at = Number(raw);
        if (!Number.isFinite(at)) {
            storage.removeItem(storageKey(pathname));
            return false;
        }
        if (now - at < RELOAD_WINDOW_MS) return true;
        storage.removeItem(storageKey(pathname));
        return false;
    } catch {
        return false;
    }
}

export function markReloadAttempt(pathname: string, now: number = Date.now()): void {
    const storage = safeStorage();
    if (!storage) return;
    try {
        storage.setItem(storageKey(pathname), String(now));
    } catch {
        // QuotaExceeded o. ä. — dann greift nur der In-Memory-Marker.
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// URL-Handling
// ─────────────────────────────────────────────────────────────────────────────

/** Hängt den Cache-Buster an (Hash bleibt erhalten). */
export function buildReloadUrl(href: string, now: number = Date.now()): string {
    const url = new URL(href);
    url.searchParams.set(RELOAD_PARAM, String(now));
    return url.toString();
}

/**
 * Entfernt den Cache-Buster wieder. Liefert die bereinigte URL oder `null`,
 * wenn der Parameter gar nicht vorhanden war.
 */
export function stripReloadParam(href: string): string | null {
    const url = new URL(href);
    if (!url.searchParams.has(RELOAD_PARAM)) return null;
    url.searchParams.delete(RELOAD_PARAM);
    return url.toString();
}

/**
 * Löst für einen Chunk-Ladefehler genau einmal pro Minute und Pfad einen
 * harten Reload mit Cache-Buster aus. Liefert `true`, wenn der Reload
 * angestoßen wurde (der Aufrufer sollte dann nichts mehr rendern/melden),
 * sonst `false` (kein Chunk-Fehler, SSR, oder Versuch bereits verbraucht —
 * dann gehört die normale Fehlerseite angezeigt).
 */
export function reloadForChunkLoadError(error: unknown, now: number = Date.now()): boolean {
    if (!canReloadForChunkLoadError(error, now)) return false;
    markReloadAttempt(window.location.pathname, now);
    const target = buildReloadUrl(window.location.href, now);
    // Sichtbar im Browser-Log, damit die Häufigkeit nachvollziehbar bleibt.
    console.info(`[chunk-load-error] Fehlender Chunk, lade einmal neu: ${target}`, error);
    window.location.replace(target);
    return true;
}

/**
 * Reine Vorprüfung ohne Seiteneffekt: Browser, Chunk-Fehler und für diesen
 * Pfad noch kein Reload innerhalb des Fensters. Auf dem Server immer `false`.
 */
export function canReloadForChunkLoadError(error: unknown, now: number = Date.now()): boolean {
    if (typeof window === 'undefined') return false;
    if (!isChunkLoadError(error)) return false;
    // Trägt die URL den Parameter noch, IST dieses Dokument bereits der Reload.
    // Das ist unabhängig von Effekt-Reihenfolge und sessionStorage: Die
    // Cleanup-Komponente im Root-Layout hat ihn noch nicht entfernt (gleicher
    // Commit wie der Fehler) oder entfernt ihn nie (global-error.tsx: Das
    // Root-Layout wird nicht committed). Ohne diese Prüfung gäbe es im zweiten
    // Fall eine Endlosschleife, wenn der Fehler auch mit frischem HTML bleibt.
    if (hrefCarriesReloadParam(window.location.href)) return false;
    return !hasRecentReloadAttempt(window.location.pathname, now);
}

function hrefCarriesReloadParam(href: string): boolean {
    try {
        return new URL(href).searchParams.has(RELOAD_PARAM);
    } catch {
        return false;
    }
}

/**
 * Beim Start eines Dokuments aufrufen (Root-Layout): Trägt die URL den
 * Cache-Buster, wird er per `history.replaceState` entfernt und das Dokument
 * als „Ergebnis eines Reloads" markiert. Liefert `true`, wenn ein Parameter
 * entfernt wurde.
 *
 * `replaceState(null, …)`: Next patcht die History-API und kopiert bei
 * `data == null` seine Internals (`__NA`, Routerbaum) in den neuen State und
 * synchronisiert `usePathname`/`useSearchParams`
 * (app-router.js → `copyNextJsInternalHistoryState`). Würde man
 * `window.history.state` durchreichen, nähme Next den Internals-Kurzschluss
 * und der Router sähe die bereinigte URL nicht.
 */
export function consumeReloadMarkerFromUrl(now: number = Date.now()): boolean {
    if (typeof window === 'undefined') return false;
    let cleaned: string | null;
    let stamped: number;
    try {
        const url = new URL(window.location.href);
        stamped = Number(url.searchParams.get(RELOAD_PARAM));
        cleaned = stripReloadParam(url.toString());
    } catch {
        return false;
    }
    if (cleaned === null) return false;
    const at = Number.isFinite(stamped) && stamped > 0 ? stamped : now;
    documentReloadMarker = {pathname: window.location.pathname, at};
    markReloadAttempt(window.location.pathname, at);
    try {
        window.history.replaceState(null, '', cleaned);
    } catch {
        // Eine URL mit Parameter ist nur unschön, kein Fehler.
    }
    return true;
}
